import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { action, observable } from 'mobx';
import { bind } from './Bind';
import { IAccessTokenStore } from './HttpClient';
import { JsonHubProtocol } from './JsonHubProtocol';
import { IDisposable } from './Mixins';
import { Mutex } from './Mutex';
import { ReconnectPolicy } from './SignalrUtils';

class ConnectionHandle implements IDisposable {
    public constructor(manager: ConnectionManager) {
        this.manager = manager;
    }

    public dispose(): void {
        this.isDisposed = true;

        this.manager.release();
    }

    public isDisposed: boolean = false;
    private manager: ConnectionManager;
}

export class ConnectionManager {
    public constructor(url: string, accessTokenStore: IAccessTokenStore) {
        this.connection = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: async () => (await accessTokenStore.getAccessToken()) || '',
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect(new ReconnectPolicy())
            .configureLogging(LogLevel.Warning)
            .withHubProtocol(new JsonHubProtocol())
            .build();

        this.connection.onclose(this.onClose);
        this.connection.onreconnecting(this.onReconnecting);
        this.connection.onreconnected(this.onReconnected);
    }

    @bind
    @action
    public async connect(): Promise<IDisposable> {
        await this.connectLock.waitAsync();

        try {
            if (!this.connected) {
                await this.connection.start();

                this.connected = true;
                this.reconnecting = false;
                this.connectionError = undefined;
            }

            this.connections++;

            return new ConnectionHandle(this);
        } finally {
            this.connectLock.release();
        }
    }

    @bind
    @action
    public async release() {
        await this.connectLock.waitAsync();

        try {
            this.connections--;

            if (this.connections <= 0) {
                await this.connection.stop();

                this.connected = false;
                this.reconnecting = false;
                this.connectionError = undefined;
            }
        } catch (e: any) {
            console.error('Failed to release connection', e);
        } finally {
            this.connectLock.release();
        }
    }

    @bind
    @action
    private onClose(error?: Error) {
        this.connected = false;
        this.reconnecting = false;
        this.connectionError = error;
    }

    @bind
    @action
    private onReconnecting(error?: Error) {
        this.connected = false;
        this.reconnecting = true;
        this.connectionError = error;
    }

    @bind
    @action
    private onReconnected() {
        this.connected = true;
        this.reconnecting = false;
        this.connectionError = undefined;
    }

    public connection: HubConnection;
    @observable
    public connected: boolean = false;
    @observable
    public reconnecting: boolean = false;
    @observable
    public connectionError?: Error;
    private connections: number = 0;
    private readonly connectLock = new Mutex();
}
